import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AppLocale } from '@/lib/i18n'
import {
  datePlaceholder,
  formatDisplayDate,
  parseDisplayDate,
} from '@/lib/date-input'

import { DatePickerButton } from './date-picker-button'

interface LocaleDateFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  error?: string
  disabled?: boolean
  autoComplete?: string
  min?: string
  max?: string
}

/**
 * Date-only field whose visible order follows the app locale (D-050), not the
 * browser locale. The form keeps the canonical `YYYY-MM-DD` value internally.
 * A native hidden date input backs the calendar button so the picker remains
 * available without letting the browser decide the visible date order.
 */
export function LocaleDateField({
  label,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  autoComplete = 'bday',
  min,
  max,
}: LocaleDateFieldProps) {
  const { i18n, t } = useTranslation()
  const locale: AppLocale = i18n.language === 'en' ? 'en' : 'it'
  const id = useId()
  const errorId = `${id}-error`
  const [draft, setDraft] = useState<string | null>(null)
  const [localError, setLocalError] = useState(false)
  const display = draft ?? (value ? formatDisplayDate(value, locale) : '')
  const shownError = localError
    ? t('auth.validation.birthDateFormat', {
        format: datePlaceholder(locale),
      })
    : error

  function commit(raw: string) {
    const next = raw.slice(0, 10)
    setDraft(next)
    if (!next.trim()) {
      setLocalError(false)
      onChange('')
      return
    }
    const parsed = parseDisplayDate(next, locale)
    if (!parsed) {
      setLocalError(true)
      onChange('')
      return
    }
    setLocalError(false)
    setDraft(null)
    onChange(parsed)
  }

  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete={autoComplete}
          maxLength={10}
          required
          disabled={disabled}
          value={display}
          onChange={(event) => commit(event.target.value)}
          onBlur={onBlur}
          placeholder={datePlaceholder(locale)}
          aria-invalid={Boolean(shownError) || undefined}
          aria-describedby={shownError ? errorId : undefined}
          className={`field-input pr-12 ${shownError ? 'border-danger' : ''}`}
        />
        <DatePickerButton
          label={label}
          value={value}
          onChange={commit}
          min={min}
          max={max}
          disabled={disabled}
        />
      </div>
      {shownError ? (
        <p id={errorId} className="mt-1.5 text-sm font-semibold text-danger">
          {shownError}
        </p>
      ) : null}
    </div>
  )
}
